import { addressToScriptHash, } from '@neo-one/client-common';
import BigNumber from 'bignumber.js';
import { RelayTransactionError } from '../errors';
import { JSONRPCProvider } from './JSONRPCProvider';
export class JSONRPCClient {
    constructor(provider) {
        this.provider = provider;
    }
    async getClaimable(address) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getclaimable',
            params: [address],
        }));
    }
    async getAccount(address) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getaccountstate',
            params: [address],
        }));
    }
    async getAsset(hash) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getassetstate',
            params: [hash],
        }));
    }
    async getBlock(hashOrIndex, options = {}) {
        const { timeoutMS } = options;
        return this.withInstance(async (provider) => provider.request({
            method: 'getblock',
            params: [hashOrIndex, 1],
            watchTimeoutMS: timeoutMS,
        }));
    }
    async getBestBlockHash() {
        return this.withInstance(async (provider) => provider.request({ method: 'getbestblockhash' }));
    }
    async getBlockCount() {
        return this.withInstance(async (provider) => provider.request({ method: 'getblockcount' }));
    }
    async getContract(address) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getcontractstate',
            params: [addressToScriptHash(address)],
        }));
    }
    async getMemPool() {
        return this.withInstance(async (provider) => provider.request({ method: 'getrawmempool' }));
    }
    async getTransaction(hash) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getrawtransaction',
            params: [hash, 1],
        }));
    }
    async getUnspentOutput(input) {
        return this.withInstance(async (provider) => provider.request({
            method: 'gettxout',
            params: [input.txid, input.vout],
        }));
    }
    async getUnspents(address) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getunspents',
            params: [address],
        }));
    }
    async testInvokeRaw(script, verifications = []) {
        return this.withInstance(async (provider) => provider.request({
            method: 'invokescript',
            params: [script, ...verifications],
        }));
    }
    async relayTransaction(value) {
        return this.withInstance(async (provider) => provider
            .request({
            method: 'relaytransaction',
            params: [value],
        })
            .catch((error) => {
            const [message, code] = error.message.split(':');
            if (error.code === 'JSON_RPC' && code === '-110') {
                throw new RelayTransactionError(message);
            }
            throw error;
        }));
    }
    async sendRawTransaction(value) {
        return this.withInstance(async (provider) => provider
            .request({
            method: 'sendrawtransaction',
            params: [value],
        })
            .catch((error) => {
            const [message, code] = error.message.split(':');
            if (error.code === 'JSON_RPC' && code === '-110') {
                throw new RelayTransactionError(message);
            }
            throw error;
        }));
    }
    async getOutput(input) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getoutput',
            params: [input.txid, input.vout],
        }));
    }
    async getClaimAmount(input) {
        return this.withInstance(async (provider) => provider
            .request({
            method: 'getclaimamount',
            params: [input.txid, input.vout],
        })
            .then((res) => new BigNumber(res)));
    }
    async getAllStorage(address) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getallstorage',
            params: [addressToScriptHash(address)],
        }));
    }
    async testInvocation(value) {
        return this.withInstance(async (provider) => provider.request({
            method: 'testinvocation',
            params: [value],
        }));
    }
    async getTransactionReceipt(hash, options = {}) {
        const { timeoutMS } = options;
        return this.withInstance(async (provider) => provider.request({
            method: 'gettransactionreceipt',
            params: [hash],
            watchTimeoutMS: timeoutMS,
        }));
    }
    async getTransactionHeight(hash) {
        return this.withInstance(async (provider) => provider.request({
            method: 'gettransactionheight',
            params: [hash],
        }));
    }
    async getInvocationData(hash) {
        return this.withInstance(async (provider) => provider.request({
            method: 'getinvocationdata',
            params: [hash],
        }));
    }
    async getConnectedPeers() {
        return this.withInstance(async (provider) => provider
            .request({
            method: 'getpeers',
        })
            .then((result) => result.connected));
    }
    async getNetworkSettings() {
        return this.withInstance(async (provider) => provider.request({
            method: 'getnetworksettings',
        }));
    }
    async runConsensusNow() {
        return this.withInstance(async (provider) => provider.request({
            method: 'runconsensusnow',
        }));
    }
    async updateSettings(options) {
        return this.withInstance(async (provider) => provider.request({
            method: 'updatesettings',
            params: [options],
        }));
    }
    async getSettings() {
        return this.withInstance(async (provider) => provider.request({
            method: 'getsettings',
        }));
    }
    async fastForwardOffset(seconds) {
        return this.withInstance(async (provider) => provider.request({
            method: 'fastforwardoffset',
            params: [seconds],
        }));
    }
    async fastForwardToTime(seconds) {
        return this.withInstance(async (provider) => provider.request({
            method: 'fastforwardtotime',
            params: [seconds],
        }));
    }
    async reset() {
        return this.withInstance(async (provider) => provider.request({ method: 'reset' }));
    }
    async getNEOTrackerURL() {
        return this.withInstance(async (provider) => provider.request({ method: 'getneotrackerurl' }));
    }
    async resetProject() {
        return this.withInstance(async (provider) => provider.request({ method: 'resetproject' }));
    }
    async withInstance(func) {
        if (this.provider instanceof JSONRPCProvider) {
            return func(this.provider);
        }
        const instance = await this.provider.getInstance();
        return func(instance);
    }
}

//# sourceMappingURL=JSONRPCClient.js.map
